import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ApiDatabase from '../../../server';
import {
  generateEmployeeColor,
  dateWordMonthShortHour,
} from '../../../helper/helper';
import { Building, PersonIcon, ExternalLink } from '../../../assets/icons';
import UseContext from '../../../hooks/useContext';
import useModal from '../../../components/modal/useModal';
import {
  formatScheduleCompact,
  ScheduleCompactBlock,
} from '../../../components/scheduleCompact/ScheduleCompactDisplay';

const EVENT_CONFIG = {
  creation_mission: { label: 'a créé la mission' },
  attribuer_mission: { label: 'a attribué la mission' },
  desassigner_mission: { label: 'a désassigné un candidat de la mission' },
  refuser_candidat_mission: { label: 'a refusé un candidat' },
  modifier_motif_refus_candidat: {
    label: "a modifié le motif de refus d'un candidat",
  },
  attribuer_mission_invitation: {
    label: "a accepté l'invitation de la mission",
  },
  invite_candidats_mission: {
    label: 'a invité des candidats',
  },
  refuser_candidat_mission_invitation: {
    label: "a refusé l'invitation d'un candidat",
  },
  pending_contact_candidat_mission: {
    label: 'a mis un candidat en attente',
  },
  modifier_detail_mission: { label: 'a modifié le détail de la mission' },
  cancel_mission: { label: 'a annulé la mission' },
  delete_mission: { label: 'a supprimé la mission' },
  notification_mission: { label: 'a envoyé une notification' },
  assignation_staff: { label: 'a assigné le suivi de la mission' },
  suspend_mission: { label: 'a suspendu le recrutement de la mission' },
  cancel_recruitment_mission: { label: 'a annulé le recrutement de la mission' },
  resume_mission: { label: 'a remis la mission en ligne' },
  interrupt_mission: { label: 'a interrompu la mission' },
  noshow_unassign_mission: { label: "a désattribué l'intérimaire suite à un No-Show" },
  creation_mission_from_interruption: { label: 'a créé la mission suite à une interruption' },
  company_cancel_mission: { label: "a annulé la mission (entreprise)" },
  company_interrupt_mission: { label: "a interrompu la mission (entreprise)" },
  tempworker_cancel_mission: { label: "a désisté de la mission (intérimaire)" },
  tempworker_interrupt_mission: { label: "a interrompu la mission (intérimaire)" },
  create_amendment_request: { label: "a envoyé une demande de prolongation" },
  amendment_accepted: { label: "a accepté la demande de prolongation" },
  amendment_refused: { label: "a refusé la demande de prolongation" },
  generate_amendment: { label: "a généré un avenant de prolongation" },
  duplicate_mission_from_amendment: { label: "a dupliqué la mission" },
  duplicate_mission: { label: "a dupliqué la mission" },
  flexibility_used: { label: "a utilisé la souplesse de la mission" },
};

const FIELD_LABELS = {
  qualification: 'Qualification',
  period: 'Période',
  schedule: 'Planning',
  salary: 'Salaire',
  description: 'Description',
  contact: 'Contact',
  licences: 'Permis',
  languages: 'Langues',
  tools: 'Outils',
  reference: 'Référence',
  other: 'Motif',
  other_interim: 'Motif',
  activity: 'Activité',
  conditions: 'Conditions',
  address: 'Adresse',
};

const formatFieldValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    const isSchedule =
      value[0]?.days && Array.isArray(value[0].days);
    if (isSchedule) return null;
    return value
      .map((v) => (typeof v === 'string' ? v : JSON.stringify(v)))
      .join(', ');
  }
  if (typeof value === 'object') {
    if (value.start && value.end) {
      const fmtDate = (d) => {
        const dt = new Date(d);
        if (isNaN(dt.getTime())) return String(d);
        return dt.toLocaleDateString('fr-FR');
      };
      return `${fmtDate(value.start)} → ${fmtDate(value.end)}`;
    }
    if (value.wage_per_hour !== undefined) return `${value.wage_per_hour}€/h`;
    if (value.name !== undefined)
      return value.name + (value.poste ? ` (${value.poste})` : '');
    if (value.street !== undefined || value.city !== undefined) {
      const parts = [
        value.street,
        value.complement,
        [value.postcode, value.city].filter(Boolean).join(' '),
        value.department,
      ].filter(Boolean);
      return parts.join(', ');
    }
    return JSON.stringify(value);
  }
  return String(value);
};

const getActivityText = (activity) => {
  const config = EVENT_CONFIG[activity.type];
  let label = config?.label || activity.type;
  let extra = '';
  let linkData = null;

  const candidateName = activity.details?.candidate
    ? `${activity.details.candidate.firstname || ''} ${activity.details.candidate.lastname || ''}`.trim()
    : '';

  switch (activity.type) {
    case 'attribuer_mission':
      if (candidateName) extra = `à ${candidateName}`;
      break;
    case 'attribuer_mission_invitation':
      break;
    case 'desassigner_mission':
    case 'refuser_candidat_mission_invitation':
    case 'modifier_motif_refus_candidat':
      break;
    case 'refuser_candidat_mission': {
      const estRefus = activity.details?.refused !== false;
      label = estRefus
        ? 'a refusé un candidat'
        : 'a retiré un candidat des refus';
      break;
    }
    case 'cancel_mission':
    case 'delete_mission':
      if (activity.details?.reason)
        extra = `— Raison : ${activity.details.reason}`;
      break;
    case 'suspend_mission':
      break;
    case 'resume_mission':
      break;
    case 'cancel_recruitment_mission':
      if (activity.details?.reason)
        extra = `— Motif : ${activity.details.reason}`;
      break;
    case 'interrupt_mission':
      if (activity.details?.reason)
        extra = `— Motif : ${activity.details.reason}`;
      break;
    case 'noshow_unassign_mission':
      break;
    case 'creation_mission_from_interruption':
      if (activity.details?.originalMissionId)
        linkData = {
          missionId: activity.details.originalMissionId,
          text: `— Mission d'origine : n°${activity.details.originalMissionIdMission}`,
        };
      break;
    case 'company_cancel_mission':
      if (activity.details?.reason)
        extra = `— Motif : ${activity.details.reason}`;
      break;
    case 'company_interrupt_mission':
      if (activity.details?.reason)
        extra = `— Motif : ${activity.details.reason}`;
      if (activity.details?.duplicatedMissionId)
        linkData = {
          missionId: activity.details.duplicatedMissionId,
          text: `— Mission de remplacement`,
        };
      break;
    case 'tempworker_cancel_mission':
      if (activity.details?.reason)
        extra = `— Motif : ${activity.details.reason}`;
      if (activity.details?.description)
        extra += ` — ${activity.details.description}`;
      break;
    case 'tempworker_interrupt_mission':
      if (activity.details?.reason)
        extra = `— Motif : ${activity.details.reason}`;
      if (activity.details?.description)
        extra += ` — ${activity.details.description}`;
      if (activity.details?.duplicatedMissionId)
        linkData = {
          missionId: activity.details.duplicatedMissionId,
          text: `— Mission de remplacement`,
        };
      break;
    case 'notification_mission': {
      const nbUsers = activity.details?.nbUsers || 0;
      const plural = nbUsers > 1 ? 's' : '';
      const vivierNames = activity.details?.vivierNames;
      if (activity.details?.notificationType === 'vivier' && vivierNames?.length > 0) {
        label = `a envoyé une notification à ${vivierNames.length} vivier${vivierNames.length > 1 ? 's' : ''} (${nbUsers} intérimaire${plural})`;
      } else {
        label = `a envoyé une notification globale (${nbUsers} intérimaire${plural})`;
      }
      break;
    }
    case 'assignation_staff': {
      const employee = activity.details?.employee;
      if (employee) {
        label = 'a assigné le suivi de la mission';
        extra =
          `à ${employee.firstname || ''} ${employee.lastname || ''}`.trim();
      } else {
        label = 'a retiré l\'assignation du suivi de la mission';
      }
      break;
    }
    case 'pending_contact_candidat_mission': {
      const enAttente = activity.details?.pendingContact !== false;
      label = enAttente
        ? 'a mis un candidat en attente'
        : 'a retiré un candidat de l\'attente';
      break;
    }
    case 'duplicate_mission_from_amendment':
    case 'duplicate_mission':
      if (activity.details?.duplicatedMissionId)
        linkData = {
          missionId: activity.details.duplicatedMissionId,
          text: `— Mission créée : n°${activity.details.duplicatedMissionIdMission || ''}`,
        };
      else if (activity.details?.originalMissionId)
        linkData = {
          missionId: activity.details.originalMissionId,
          text: `— Mission d'origine : n°${activity.details.originalMissionIdMission || ''}`,
        };
      if (activity.details?.reason) {
        const reasons = {
          flexibility_exhausted: '(souplesse épuisée)',
          max_amendments: '(max avenants atteint)',
          max_duration: '(durée max 18 mois dépassée)',
        };
        extra = reasons[activity.details.reason] || '';
      }
      break;
    default:
      break;
  }

  return { label, extra, linkData };
};

const GROUPABLE_TYPES = new Set([
  'refuser_candidat_mission',
  'refuser_candidat_mission_invitation',
  'modifier_motif_refus_candidat',
  'desassigner_mission',
]);

const getAdminKey = (admin) =>
  admin ? `${admin.firstname || ''}|${admin.lastname || ''}` : '';

/** Évite de fusionner refus et annulations de refus dans un même bloc groupé. */
const getGroupKey = (activity) => {
  const base = `${activity.type}|${getAdminKey(activity.admin)}`;
  if (activity.type === 'refuser_candidat_mission') {
    const estRefus = activity.details?.refused !== false;
    return `${base}|ref:${estRefus}`;
  }
  return base;
};

/** Clé stable pour ne compter qu’une fois le même candidat dans un groupe d’événements. */
const getCandidateDedupeKey = (activity, rowIndex) => {
  const id = activity.details?.idCandidate;
  if (id != null && id !== '') {
    const s =
      typeof id === 'object' && typeof id.toString === 'function'
        ? id.toString()
        : String(id);
    if (s && s !== '[object Object]') return `id:${s}`;
  }
  const c = activity.details?.candidate;
  if (c) {
    const name = `${c.firstname || ''} ${c.lastname || ''}`
      .trim()
      .toLowerCase();
    if (name) return `name:${name}`;
  }
  return `row:${rowIndex}`;
};

const collectUniqueCandidatesFromGroup = (items) => {
  const byKey = new Map();
  items.forEach((a, i) => {
    const key = getCandidateDedupeKey(a, i);
    if (byKey.has(key)) return;
    const c = a.details?.candidate;
    const label = c
      ? `${c.firstname || ''} ${c.lastname || ''}`.trim()
      : '';
    byKey.set(key, label || null);
  });
  const names = [...byKey.values()].filter(Boolean);
  return { uniqueCount: byKey.size, names };
};

const groupActivities = (activities) => {
  if (!activities || activities.length === 0) return [];
  const groups = [];
  let current = null;

  for (const activity of activities) {
    const key = getGroupKey(activity);
    if (
      current &&
      current.key === key &&
      GROUPABLE_TYPES.has(activity.type)
    ) {
      current.items.push(activity);
    } else {
      current = { key, items: [activity] };
      groups.push(current);
    }
  }

  return groups.map((g) => {
    if (g.items.length === 1) return g.items[0];
    const first = g.items[0];
    const { uniqueCount, names: candidateNames } =
      collectUniqueCandidatesFromGroup(g.items);
    return {
      ...first,
      _grouped: true,
      _count: g.items.length,
      _uniqueCandidateCount: uniqueCount,
      _candidates: candidateNames,
      _lastDate: g.items[g.items.length - 1].date,
    };
  });
};

const getGroupedText = (activity) => {
  const config = EVENT_CONFIG[activity.type];
  const count =
    activity._uniqueCandidateCount != null
      ? activity._uniqueCandidateCount
      : activity._count;

  switch (activity.type) {
    case 'refuser_candidat_mission': {
      const estRefus = activity.details?.refused !== false;
      if (estRefus) {
        return `a refusé ${count} candidat${count > 1 ? 's' : ''}`;
      }
      return `a retiré ${count} candidat${count > 1 ? 's' : ''} des refus`;
    }
    case 'refuser_candidat_mission_invitation':
      return `a refusé l'invitation de ${count} candidat${count > 1 ? 's' : ''}`;
    case 'modifier_motif_refus_candidat':
      return `a modifié le motif de refus de ${count} candidat${count > 1 ? 's' : ''}`;
    case 'desassigner_mission':
      return `a désassigné ${count} candidat${count > 1 ? 's' : ''} de la mission`;
    default:
      return config?.label || activity.type;
  }
};

const ActivityCard = ({ activity, isLast }) => {
  const [, setSearchParams] = useSearchParams();
  const { setAdminType, setAdminFileName, setAdminUserId } = UseContext();
  const { toggle } = useModal();
  const isCompany = activity.typeUser === 'user';
  const isInvitationAccepted = activity.type === 'attribuer_mission_invitation';

  const candidateName = activity.details?.candidate
    ? `${activity.details.candidate.firstname || ''} ${activity.details.candidate.lastname || ''}`.trim()
    : '';

  const displayName = isInvitationAccepted && candidateName
    ? candidateName
    : activity.admin
      ? `${activity.admin.firstname || ''} ${activity.admin.lastname || ''}`.trim()
      : isCompany
        ? 'Entreprise'
        : 'Administrateur';

  const adminInitials = isInvitationAccepted && candidateName
    ? `${(activity.details.candidate.firstname || '')[0] || ''}${(activity.details.candidate.lastname || '')[0] || ''}`.toUpperCase()
    : activity.admin
      ? `${(activity.admin.firstname || '')[0] || ''}${(activity.admin.lastname || '')[0] || ''}`.toUpperCase()
      : 'A';

  const avatarPerson = isInvitationAccepted && activity.details?.candidate
    ? activity.details.candidate
    : activity.admin;
  const avatarColor = avatarPerson
    ? generateEmployeeColor(avatarPerson.firstname, avatarPerson.lastname)
    : generateEmployeeColor(displayName, '');
  const isGrouped = activity._grouped;
  const { label, extra, linkData } = isGrouped
    ? { label: getGroupedText(activity), extra: '', linkData: null }
    : getActivityText(activity);

  const dateLabel = isGrouped && activity._lastDate
    ? `${dateWordMonthShortHour(activity._lastDate)} – ${dateWordMonthShortHour(activity.date)}`
    : dateWordMonthShortHour(activity.date);

  const showUserIcon = isInvitationAccepted;

  return (
    <div className="group">
      <div className="flex gap-3 px-3 py-4 hover:bg-gray-50 transition-colors rounded-lg">
        <div className="flex-shrink-0 pt-0.5">
          {showUserIcon ? (
            <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-100 border border-gray-200">
              <PersonIcon wh={16} color="#6B7280" />
            </div>
          ) : isCompany ? (
            <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-100 border border-gray-200">
              <Building wh={16} color="#6B7280" />
            </div>
          ) : (
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-medium text-white"
              style={{ backgroundColor: avatarColor }}
            >
              {adminInitials}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-900">
              {displayName}
            </span>
            <span className="text-xs text-gray-400">
              · {dateLabel}
            </span>
          </div>
          <p className="text-sm text-gray-600 mt-0">
            {label}
            {extra && <span className="text-gray-500"> {extra}</span>}
            {linkData && (
              <span
                className="text-yellow-600 font-semibold cursor-pointer hover:underline ml-1"
                onClick={() => {
                  setSearchParams((prev) => {
                    prev.set('mission', linkData.missionId);
                    return prev;
                  });
                }}
              >
                {linkData.text}
              </span>
            )}
          </p>
          {activity.type === 'tempworker_interrupt_mission' &&
            (activity.details?.fileName || activity.details?.filePath) && (
              <div className="mt-1">
                <span
                  className="inline-flex items-center gap-1 text-sm text-blue-600 cursor-pointer hover:underline"
                  onClick={() => {
                    const resolvedFileName = activity.details.fileName
                      || activity.details.filePath?.split('/').pop();
                    setAdminType('otherDocument');
                    setAdminFileName(resolvedFileName);
                    setAdminUserId(activity.admin?._id);
                    toggle('ad_file_view');
                  }}
                >
                  <ExternalLink wh={14} color="#235fe6" />
                  Voir l'avis d'inaptitude
                </span>
              </div>
            )}
          {isGrouped &&
            activity._candidates &&
            activity._candidates.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {activity._candidates.map((name, i) => (
                  <span
                    key={i}
                    className="text-xs text-gray-600 px-1.5 py-0.5 border border-gray-200 rounded-md"
                  >
                    {name}
                  </span>
                ))}
              </div>
            )}
          {!isGrouped &&
            (GROUPABLE_TYPES.has(activity.type) ||
              activity.type === 'pending_contact_candidat_mission') &&
            activity.details?.candidate && (
              <div className="flex flex-wrap gap-1 mt-1">
                <span
                  className="text-xs text-gray-600 px-1.5 py-0.5 border border-gray-200"
                  style={{ borderRadius: '2px' }}
                >
                  {`${activity.details.candidate.firstname || ''} ${activity.details.candidate.lastname || ''}`.trim()}
                </span>
              </div>
            )}
          {activity.type === 'notification_mission' &&
            activity.details?.notificationType === 'vivier' &&
            activity.details?.vivierNames?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {activity.details.vivierNames.map((name, i) => (
                  <span
                    key={i}
                    className="text-xs text-gray-600 px-1.5 py-0.5 border border-gray-200"
                    style={{ borderRadius: '2px' }}
                  >
                    {name}
                  </span>
                ))}
              </div>
            )}
          {activity.type === 'invite_candidats_mission' &&
            activity.details?.invitees?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {activity.details.invitees.map((invitee, i) => (
                  <span
                    key={invitee.idCandidate || i}
                    className="text-xs text-gray-600 px-1.5 py-0.5 border border-gray-200"
                    style={{ borderRadius: '2px' }}
                  >
                    {`${invitee.firstname || ''} ${invitee.lastname || ''}`.trim()}
                  </span>
                ))}
              </div>
            )}
          {activity.type === 'modifier_detail_mission' &&
            Array.isArray(activity.details?.modifications) &&
            activity.details.modifications.length > 0 && (
              <div className="mt-2 border border-gray-100 rounded-lg overflow-hidden">
                {activity.details.modifications.map((change, i) => {
                  const isSchedule = change.field === 'schedule';
                  const beforeSchedule =
                    isSchedule && Array.isArray(change.before)
                      ? formatScheduleCompact(change.before)
                      : null;
                  const afterSchedule =
                    isSchedule && Array.isArray(change.after)
                      ? formatScheduleCompact(change.after)
                      : null;

                  return (
                    <div
                      key={i}
                      className={`flex flex-col gap-2 px-3 py-2 text-xs ${
                        i !== activity.details.modifications.length - 1
                          ? 'border-b border-gray-100'
                          : ''
                      }`}
                    >
                      <span className="font-medium text-gray-700">
                        {FIELD_LABELS[change.field] || change.field}
                      </span>
                      {isSchedule && (beforeSchedule || afterSchedule) ? (
                        <div className="flex gap-4 flex-wrap">
                          <div className="flex-1 min-w-[140px]">
                            <div className="text-[10px] uppercase text-red-600 font-medium mb-1">
                              Avant
                            </div>
                            {beforeSchedule && beforeSchedule.length > 0 ? (
                              <ScheduleCompactBlock data={beforeSchedule} />
                            ) : (
                              <span className="text-gray-400 text-[11px]">
                                Aucun planning
                              </span>
                            )}
                          </div>
                          <div className="flex-1 min-w-[140px]">
                            <div className="text-[10px] uppercase text-green-600 font-medium mb-1">
                              Après
                            </div>
                            {afterSchedule && afterSchedule.length > 0 ? (
                              <ScheduleCompactBlock data={afterSchedule} />
                            ) : (
                              <span className="text-gray-400 text-[11px]">
                                Aucun planning
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="bg-red-50 text-red-600 px-1.5 py-0.5 rounded line-through">
                            {formatFieldValue(change.before)}
                          </span>
                          <span className="text-gray-400">→</span>
                          <span className="bg-green-50 text-green-700 px-1.5 py-0.5 rounded">
                            {formatFieldValue(change.after)}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
        </div>
      </div>
      {!isLast && <hr className="border-gray-100" />}
    </div>
  );
};

const MissionActivityTab = ({ missionId, refreshNonce = 0 }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchActivities = (pageNum, append = false) => {
    const token = localStorage.getItem('xsrfToken');
    setLoading(true);
    ApiDatabase.getMissionActivityLogs(
      { token, idMission: missionId, page: pageNum, limit: 30 },
      (data) => {
        if (append) {
          setActivities((prev) => [...prev, ...data.activities]);
        } else {
          setActivities(data.activities);
        }
        setTotalPages(data.totalPages);
        setTotal(data.total);
        setLoading(false);
      },
      (err) => {
        if (err.name !== 'CanceledError') {
          console.error(err);
        }
        setLoading(false);
      }
    );
  };

  useEffect(() => {
    if (missionId) {
      setPage(1);
      fetchActivities(1);
    }
  }, [missionId, refreshNonce]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchActivities(nextPage, true);
  };

  if (loading && activities.length === 0) {
    return (
      <div className="flex flex-col gap-4 py-4 px-2">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="animate-pulse flex gap-3">
            <div className="w-8 h-8 bg-gray-200 rounded-full flex-shrink-0" />
            <div className="flex-1 flex flex-col gap-2">
              <div className="flex gap-2">
                <div className="w-24 h-4 bg-gray-200 rounded" />
                <div className="w-16 h-4 bg-gray-200 rounded-full" />
              </div>
              <div className="w-3/4 h-3 bg-gray-200 rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!loading && activities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <ClockEmptyIcon />
        </div>
        <div className="text-gray-900 font-semibold text-base mb-1">
          Aucune activité
        </div>
        <p className="text-gray-500 text-sm text-center">
          Aucune activité n'a encore été enregistrée pour cette mission.
        </p>
      </div>
    );
  }

  const grouped = groupActivities(activities);

  return (
    <div className="w-full py-2">
      <div>
        {grouped.map((activity, index) => (
          <ActivityCard
            key={index}
            activity={activity}
            isLast={index === grouped.length - 1}
          />
        ))}
      </div>
      {page < totalPages && (
        <div className="flex justify-center pt-4 pb-2">
          <button
            onClick={handleLoadMore}
            disabled={loading}
            className="px-5 py-2 text-sm font-medium text-yellow-600 border border-yellow-200 rounded-full hover:bg-yellow-50 transition-colors disabled:opacity-50"
          >
            {loading ? 'Chargement...' : 'Voir plus'}
          </button>
        </div>
      )}
    </div>
  );
};

const ClockEmptyIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 8V12L15 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
      stroke="#9CA3AF"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default MissionActivityTab;
